/**
 * @file 管理平台下线迁移相关工具
 * @description 后端 userinfo 返回 workspace_offline_days（下线倒计时，剩余天数，总周期 30 天），
 *              前端据此划分三个阶段：轻量通知期、受限运行期、完全关停
 */

import {getSSOHost} from '@/api/login';

/** 下线倒计时总周期（天） */
export const MIGRATION_TOTAL_DAYS = 30;

/** 进入受限运行期的已过天数（D7 起） */
export const MIGRATION_RESTRICT_ELAPSED_DAYS = 7;

/** 进入受限运行期时的剩余天数 */
export const MIGRATION_RESTRICT_REMAINING_DAYS = MIGRATION_TOTAL_DAYS - MIGRATION_RESTRICT_ELAPSED_DAYS;

/** 迁移阶段 */
export enum MigrationPhase {
    /** 未开启下线倒计时（后端未下发该字段） */
    NONE = 'NONE',
    /** D0~D7 正常运行期，轻量通知 */
    NOTICE = 'NOTICE',
    /** D7~D30 受限运行期，强引导，禁止新建/演化 */
    RESTRICTED = 'RESTRICTED',
    /** D+30 起完全关停，重定向新平台 */
    SHUTDOWN = 'SHUTDOWN'
}

/**
 * 根据剩余天数计算所处阶段
 * @param offlineDays 剩余天数，30 倒数至 0；null 表示后端未下发
 */
export function getMigrationPhase(offlineDays: number | null): MigrationPhase {
    if (offlineDays === null || Number.isNaN(offlineDays)) {
        return MigrationPhase.NONE;
    }

    if (offlineDays <= 0) {
        return MigrationPhase.SHUTDOWN;
    }

    // 剩余天数大于 23（即已过天数不足 7 天）仍处于轻量通知期
    if (offlineDays > MIGRATION_RESTRICT_REMAINING_DAYS) {
        return MigrationPhase.NOTICE;
    }

    return MigrationPhase.RESTRICTED;
}

/** 新平台地址（与 SSO 保持同一套环境判断） */
export function getNewPlatformUrl(): string {
    return getSSOHost();
}

/** 跳转新平台 */
export function goToNewPlatform() {
    window.open(getNewPlatformUrl(), '_blank', 'noopener,noreferrer');
}

/** 重定向到新平台（完全关停阶段兜底，服务端 302 未生效时使用） */
export function redirectToNewPlatform() {
    window.location.replace(getNewPlatformUrl());
}

/** 轻量通知期弹窗：每个用户仅弹 1 次 */
const NOTICE_MODAL_KEY = 'fm_migration_notice_modal_closed';
/** 轻量通知期通栏：关闭后本阶段不再展示 */
const NOTICE_BANNER_KEY = 'fm_migration_notice_banner_closed';
/** 受限运行期弹窗：同一标签页会话内仅弹 1 次 */
const RESTRICTED_MODAL_KEY = 'fm_migration_restricted_modal_shown';

function readFlag(storage: Storage, key: string): boolean {
    try {
        return storage.getItem(key) === '1';
    } catch (err) {
        return false;
    }
}

function writeFlag(storage: Storage, key: string) {
    try {
        storage.setItem(key, '1');
    } catch (err) {
        // 隐私模式下写入失败时忽略，最多多弹一次
    }
}

/** 轻量通知期弹窗是否已被该用户关闭过 */
export function isNoticeModalClosed(userId: string): boolean {
    return readFlag(localStorage, `${NOTICE_MODAL_KEY}_${userId}`);
}

export function markNoticeModalClosed(userId: string) {
    writeFlag(localStorage, `${NOTICE_MODAL_KEY}_${userId}`);
}

/** 轻量通知期通栏是否已被该用户关闭过 */
export function isNoticeBannerClosed(userId: string): boolean {
    return readFlag(localStorage, `${NOTICE_BANNER_KEY}_${userId}`);
}

export function markNoticeBannerClosed(userId: string) {
    writeFlag(localStorage, `${NOTICE_BANNER_KEY}_${userId}`);
}

/** 受限运行期弹窗在当前标签页会话内是否已弹出过 */
export function isRestrictedModalShown(): boolean {
    return readFlag(sessionStorage, RESTRICTED_MODAL_KEY);
}

export function markRestrictedModalShown() {
    writeFlag(sessionStorage, RESTRICTED_MODAL_KEY);
}
